import { useState, useEffect, useMemo, useCallback } from 'react';

// Helper function to calculate business date
const calculateBusinessDate = () => {
  try {
    const now = new Date();
    
    // Get IST date parts directly
    const istDate = now.toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' }); // YYYY-MM-DD
    const istTimeStr = now.toLocaleTimeString('en-GB', {
      timeZone: 'Asia/Kolkata',
      hour12: false,
      hour: '2-digit',
      minute: '2-digit'
    });
    
    const [hoursStr, minutesStr] = istTimeStr.split(':');
    const hours = parseInt(hoursStr, 10);
    const minutes = parseInt(minutesStr, 10);
    
    const isBeforeBusinessStart = hours < 11 || (hours === 11 && minutes < 30);
    
    if (isBeforeBusinessStart) {
      // Calculate yesterday's date
      const [year, month, day] = istDate.split('-').map(Number);
      const yesterday = new Date(year, month - 1, day);
      yesterday.setDate(yesterday.getDate() - 1);
      
      const y = yesterday.getFullYear();
      const m = String(yesterday.getMonth() + 1).padStart(2, '0');
      const d = String(yesterday.getDate()).padStart(2, '0');
      return `${y}-${m}-${d}`;
    }
    
    return istDate;
  } catch (error) {
    console.error('Error calculating business date:', error);
    // Fallback to current date in YYYY-MM-DD format
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
};

// Helper functions for date calculations
const getWeeksInMonth = (year, month) => {
  // Safari-compatible date formatting
  const formatDateSafari = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };
  
  const weeks = [];
  const firstDay = new Date(year, month - 1, 1);
  const lastDay = new Date(year, month, 0);
  
  let currentWeekStart = new Date(firstDay);
  let weekNumber = 1;
  
  while (currentWeekStart <= lastDay) {
    const weekEnd = new Date(currentWeekStart);
    weekEnd.setDate(currentWeekStart.getDate() + 6);
    
    if (weekEnd > lastDay) {
      weekEnd.setTime(lastDay.getTime());
    }
    
    weeks.push({
      weekNumber,
      startDate: formatDateSafari(currentWeekStart),
      endDate: formatDateSafari(weekEnd),
      label: `Week ${weekNumber} (${currentWeekStart.getDate()}-${weekEnd.getDate()})`
    });
    
    currentWeekStart.setDate(currentWeekStart.getDate() + 7);
    weekNumber++;
  }
  
  return weeks;
};

/**
 * Custom hook for managing date range selection state and logic
 * 
 * @returns {Object} State and handlers for date range selection
 */
export const useDateRangeSelector = () => {
  const businessDate = calculateBusinessDate();
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1;
  
  // Helper to create valid Date object
  const createValidDateObject = (dateString) => {
    const dateObj = new Date(dateString);
    // Check if date is valid
    if (isNaN(dateObj.getTime())) {
      console.error('Invalid date string:', dateString);
      return new Date(); // Return current date as fallback
    }
    return dateObj;
  };
  
  // State management
  const [reportType, setReportType] = useState('daily');
  const [selectedDate, setSelectedDate] = useState(businessDate);
  const [selectedDateObj, setSelectedDateObj] = useState(() => createValidDateObject(businessDate));
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [selectedMonth, setSelectedMonth] = useState(currentMonth);
  const [selectedWeek, setSelectedWeek] = useState(1);
  const [customStartDate, setCustomStartDate] = useState(businessDate);
  const [customStartDateObj, setCustomStartDateObj] = useState(() => createValidDateObject(businessDate));
  const [customEndDate, setCustomEndDate] = useState(businessDate);
  const [customEndDateObj, setCustomEndDateObj] = useState(() => createValidDateObject(businessDate));

  // Compute weeks in month
  const weeksInMonth = useMemo(() => 
    getWeeksInMonth(selectedYear, selectedMonth),
    [selectedYear, selectedMonth]
  );

  // Initialize week selection when weeks change
  useEffect(() => {
    if (weeksInMonth.length > 0 && selectedWeek > weeksInMonth.length) {
      setSelectedWeek(weeksInMonth[0].weekNumber);
    }
  }, [weeksInMonth, selectedWeek]);

  // Get current business date object
  const getCurrentBusinessDateObject = useCallback(() => {
    return new Date(businessDate);
  }, [businessDate]);

  // Safari-compatible date formatting utility
  const formatDateToYYYYMMDD = useCallback((date) => {
    if (!date) return '';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }, []);

  // Handler for date selections
  const handleDateSelect = useCallback((newDate) => {
    if (newDate) {
      setSelectedDateObj(newDate);
      setSelectedDate(formatDateToYYYYMMDD(newDate));
    }
  }, [formatDateToYYYYMMDD]);

  const handleMonthlyDateSelect = useCallback((newDate) => {
    if (newDate) {
      setSelectedDateObj(newDate);
      setSelectedDate(formatDateToYYYYMMDD(newDate));
    }
  }, [formatDateToYYYYMMDD]);

  const handleYearlyDateSelect = useCallback((newDate) => {
    if (newDate) {
      setSelectedDateObj(newDate);
      setSelectedDate(formatDateToYYYYMMDD(newDate));
    }
  }, [formatDateToYYYYMMDD]);

  const handleCustomRangeSelect = useCallback((range) => {
    console.log('📅 Custom range selected:', range);
    
    if (range?.from) {
      const fromDate = new Date(range.from);
      setCustomStartDateObj(fromDate);
      const formattedFrom = formatDateToYYYYMMDD(fromDate);
      setCustomStartDate(formattedFrom);
      console.log('✅ Start date set:', formattedFrom);
      
      // If no end date selected yet, set end date same as start
      if (!range?.to) {
        setCustomEndDateObj(fromDate);
        setCustomEndDate(formattedFrom);
        console.log('✅ End date set to same as start:', formattedFrom);
      }
    }
    
    if (range?.to) {
      const toDate = new Date(range.to);
      setCustomEndDateObj(toDate);
      const formattedTo = formatDateToYYYYMMDD(toDate);
      setCustomEndDate(formattedTo);
      console.log('✅ End date set:', formattedTo);
    }
  }, [formatDateToYYYYMMDD]);

  // Handlers for year, month, and week changes
  const handleYearChange = useCallback((year) => {
    setSelectedYear(year);
    if (reportType === 'monthly') {
      const newDate = new Date(year, selectedMonth - 1, 1);
      handleMonthlyDateSelect(newDate);
    } else if (reportType === 'yearly') {
      const newDate = new Date(year, 0, 1);
      handleYearlyDateSelect(newDate);
    }
  }, [reportType, selectedMonth, handleMonthlyDateSelect, handleYearlyDateSelect]);

  const handleMonthChange = useCallback((month) => {
    setSelectedMonth(month);
    if (reportType === 'weekly') {
      const weeks = getWeeksInMonth(selectedYear, month);
      if (weeks.length > 0) {
        setSelectedWeek(weeks[0].weekNumber);
      }
    } else if (reportType === 'monthly') {
      const newDate = new Date(selectedYear, month - 1, 1);
      handleMonthlyDateSelect(newDate);
    }
  }, [reportType, selectedYear, handleMonthlyDateSelect]);

  const handleWeekChange = useCallback((week) => {
    setSelectedWeek(week);
  }, []);

  // Get date range based on report type
  const getDateRange = useCallback(() => {
    switch (reportType) {
      case 'daily':
        return { startDate: selectedDate, endDate: selectedDate };
      case 'weekly':
        const week = weeksInMonth.find(w => w.weekNumber === selectedWeek);
        return week ? { startDate: week.startDate, endDate: week.endDate } : { startDate: selectedDate, endDate: selectedDate };
      case 'monthly':
        const firstDay = new Date(selectedYear, selectedMonth - 1, 1);
        const lastDay = new Date(selectedYear, selectedMonth, 0);
        return {
          startDate: formatDateToYYYYMMDD(firstDay),
          endDate: formatDateToYYYYMMDD(lastDay)
        };
      case 'yearly':
        const yearStart = new Date(selectedYear, 0, 1);
        const yearEnd = new Date(selectedYear, 11, 31);
        return {
          startDate: formatDateToYYYYMMDD(yearStart),
          endDate: formatDateToYYYYMMDD(yearEnd)
        };
      case 'custom':
        console.log('🔍 Getting custom date range:', { customStartDate, customEndDate });
        return { startDate: customStartDate, endDate: customEndDate };
      default:
        return { startDate: selectedDate, endDate: selectedDate };
    }
  }, [reportType, selectedDate, selectedYear, selectedMonth, selectedWeek, weeksInMonth, customStartDate, customEndDate, formatDateToYYYYMMDD]);

  return {
    // State
    reportType,
    selectedDate,
    selectedDateObj,
    selectedYear,
    selectedMonth,
    selectedWeek,
    weeksInMonth,
    customStartDate,
    customStartDateObj,
    customEndDate,
    customEndDateObj,
    businessDate,
    
    // Setters
    setReportType,
    
    // Handlers
    handleDateSelect,
    handleYearChange,
    handleMonthChange,
    handleWeekChange,
    handleCustomRangeSelect,
    
    // Utilities
    getCurrentBusinessDateObject,
    getDateRange
  };
};

